import { useMutation, useClient } from '../../../../loco-sync';
import { ModelDataStore } from '@teamworksdev/loco-sync-client';
import { useUndoRedo } from '../../../utils/undoRedo';
import { BlockDraftWorkoutDomain } from '@strength/domain';
import { M } from '@strength/loco-sync';
import { defaultLayer } from '@strength/db-schema';

export const useBlockDraftWorkoutUndoRedo = (blockId: string) => {
  const mutation = useMutation();
  const store = useClient().getCache().getStore();

  const { undo, redo, addAction } = useUndoRedo<
    BlockDraftWorkoutDomain.Command[]
  >({
    key: `${BlockDraftWorkoutDomain.Module}:${blockId}`,
    getReverseAction: (commands) => getReverseCommands(commands, store),
    performAction: (commands) => {
      for (const command of commands) {
        mutation.mutate(command);
      }
    },
  });

  return {
    undo,
    redo,
    bulkMutate: (commands: readonly BlockDraftWorkoutDomain.Command[]) => {
      addAction([...commands]);
      for (const command of commands) {
        mutation.mutate(command);
      }
    },
  };
};

function getReverseCommands(
  commands: BlockDraftWorkoutDomain.Command[],
  store: ModelDataStore<M>,
): BlockDraftWorkoutDomain.Command[] | null {
  const reverseCommands: BlockDraftWorkoutDomain.Command[] = [];
  for (const command of commands) {
    const reverseCommand: BlockDraftWorkoutDomain.Command = {
      module: command.module,
      identity: command.identity,
      edits: [],
    };

    for (const edit of command.edits) {
      // Ignore creates and deletes of "BlockDraftWorkoutLayer" for purposes of undo/redo
      if (edit.action === 'create' || edit.action === 'delete') continue;

      const currentData = store.getOne(edit.modelName, {
        id: edit.modelId,
      }) ?? {
        // set some defaults here as a beginning state
        assignedDate: null,
        layer: defaultLayer(),
        name: null,
      };

      const reverseEdit: BlockDraftWorkoutDomain.Edit | null = {
        modelName: edit.modelName,
        modelId: edit.modelId,
        action: 'update',
        // Cheating here, should have a more robust solution
        data: {
          assignedDate: currentData.assignedDate,
          layer: currentData.layer,
          name: currentData.name,
        },
      };

      // Use unshift instead of push to reverse the order of edits
      reverseCommand.edits.unshift(reverseEdit);
    }

    // Use unshift instead of push to reverse the order of commands
    reverseCommands.unshift(reverseCommand);
  }

  return reverseCommands;
}
